import type { TimecodeSpec } from "@infoflyt/shared/timecode";
import { timecodeSpec } from "@infoflyt/shared/timecode";

/**
 * Oekten nettflata jobber i.
 *
 * Samme soem som i feltappen: alt leser oekten herfra, og naar Supabase og
 * auth kommer byttes `hentOekt` ut mens resten staar. Froeet speiler `FROE` i
 * apps/mobile, saa de to flatene viser samme produksjon mens vi bygger.
 */

export type Rolle =
  | "regi" | "vj" | "redaktor" | "produsent" | "klipper" | "prodleder" | "crew"
  /* Stillingen som har synkene. En ROLLE og ikke en fagfunksjon, fordi
     reporteren har noe hen skriver. Se CLAUDE.md, avgjort 24.10.2026. */
  | "reporter"
  /* Teknisk-gruppa: foto, A-foto, A-lyd, TOM, lyd, grip, shader, teknisk
     assistent. Ble en rolle 27.10.2026 av samme grunn som reporteren — det
     er teknikeren som jammer tidskoden, og en jam setter ankeret for alle.
     Skillet fra `crew` måtte til fordi koordinator og innspillingsleder bar
     samme verdi. Se migrasjon 20261027090000. */
  | "tekniker"
  /* A-foto. Jammer som teknikeren, men eier i tillegg takten og
     utstyrslista for produksjonen — én person, ikke en gruppe. Kommer med
     invitasjon. Se migrasjon 20261027190000. */
  | "afoto"
  /* Koordinatoren. Rolle fra 31.10.2026, etter samme proeve som de to
     over: hen laster opp dagsplanen, dagsmanus og kjoereplanen — det som
     gaar ut til hele crewet foer en opptaksdag, og det er like mye
     skriving som en loggpost. Og som teknikeren: `crew` kunne ikke se
     forskjell paa koordinatoren og alle andre, saa i praksis kunne
     hvem som helst sende ut en ny dagsplan. Se 20261031100000. */
  | "koordinator";

export interface Oekt {
  readonly production: {
    readonly id: string;
    readonly name: string;
    readonly framerate: "23.976" | "24" | "25" | "29.97" | "30" | "50" | "59.94" | "60";
    readonly dropFrame: boolean;
    readonly endDateIso: string;
    readonly nle: "avid" | "premiere" | "resolve" | null;
    /**
     * Logoen som gjelder — FERDIG VALGT.
     *
     * Produksjonens gaar foran selskapets; regelen er `velgLogo` i
     * `@infoflyt/shared/logo`, og den brukes ÉN gang, naar oekten bygges.
     * Flatene skal ikke kjenne til at det finnes to kandidater — da ville
     * menyen og rapporten kunne blitt uenige om hvilken som vant.
     *
     * Null betyr ingen logo, og da staar Infoflyt-merket.
     */
    readonly logoSti?: string | null;
  };
  readonly member: {
    readonly id: string;
    readonly displayName: string;
    readonly initials: string;
    readonly role: Rolle;
    /**
     * Rollene de EKSTRA fagfunksjonene gir.
     *
     * En person har en rad per produksjon, men kan baere flere
     * fagfunksjoner paa den — koordinator OG prodleder. Basen regner
     * dem i `tilgang.har_rolle`; flata maa kjenne dem for aa vise de
     * samme punktene i Oppsett. Se migrasjon 20261101110000.
     */
    readonly extraRoles?: readonly Rolle[];
    /**
     * Teamet man staar paa, eller null.
     *
     * Her fordi LOGGEN HENGER PAA DEN: er man paa et team, skriver hele
     * teamet i samme oekt, og oekt-id-en utledes av (produksjon, dag,
     * team). Uten den paa oekta maatte hver flate slaatt den opp selv, og
     * to flater som slaar opp hver sin gang kan svare ulikt.
     */
    readonly unitId?: string | null;
    /**
     * Teamets navn, eller tom streng.
     *
     * Flatene viser TEAMET foerst og personen etter — en teamlogg er
     * teamets, ikke den enkeltes. Navnet foelger med ned paa
     * medlemsraden slik at ingen flate trenger et oppslag til for aa
     * skrive et hode. Se LiveloggScreen og state/logg.ts.
     */
    readonly unitName?: string;
    /**
     * Fagfunksjonens navn, eller tom streng.
     *
     * Rollen er den grove tilgangen, fagfunksjonen den fine. Brukes bare
     * der rollen er for vid: «A-foto eller A-lyd skriver teknisk brief»,
     * mens rollen `tekniker` er hele Teknisk-gruppa. Se
     * `tilgang.har_fagfunksjon` og migrasjon 20261102150000.
     */
    readonly craftNavn?: string;
  };
  readonly shootDay: string;
  /** Datoen dagen faller paa, fra `shoot_day`. Null naar den ikke er satt. */
  readonly shootDate?: string | null;
}

const FROE: Oekt = {
  production: {
    id: "00000000-0000-4000-8000-000000000001",
    name: "Øya",
    framerate: "25",
    dropFrame: false,
    endDateIso: "2026-12-19",
    nle: null,
  },
  member: {
    id: "00000000-0000-4000-8000-000000000003",
    displayName: "Nora Lie",
    initials: "NL",
    role: "prodleder",
  },
  shootDay: "7",
};

const NOKKEL = "infoflyt.web.rolle";

export function hentOekt(): Oekt {
  try {
    const r = localStorage.getItem(NOKKEL) as Rolle | null;
    if (r !== null) return { ...FROE, member: { ...FROE.member, role: r } };
  } catch {
    // Utilgjengelig lagring. Froeet gjelder.
  }
  return FROE;
}

/**
 * STILLAS: lar oss se flata som en annen rolle uten innlogging.
 *
 * ========  DEN ER TO ULIKE TING, AVHENGIG AV AUTH  ========
 *
 * Med innlogging AV er dette det eneste som finnes. Rollen kommer fra
 * froeet, og uten en bryter kan den ikke endres i det hele tatt — og
 * flatene er rollestyrte, saa da kan halvparten av dem ikke proeves.
 *
 * Med innlogging PAA er den en knapp som LYVER: da sier
 * `production_member` hva du er, og basen haandhever det uansett hva som
 * staar her. Derfor rendres bryteren ikke i det hele tatt naar auth er
 * paa — se `Meny.tsx`. Den rydder seg selv bort.
 *
 * Fjernet 27.10.2026 fordi menyen var rotete, og satt tilbake samme dag
 * fordi det var rotet som skulle bort, ikke muligheten. Den ligger naa
 * nederst, merket som stillas.
 */
export function settRolle(r: Rolle): void {
  try {
    localStorage.setItem(NOKKEL, r);
    location.reload();
  } catch {
    // Ingenting aa gjoere.
  }
}

/**
 * Tilbake til froeet.
 *
 * Trengs fordi noekkelen OVERLEVER at bryteren forsvinner. Da bryteren var
 * borte en dag, laa verdien igjen i nettleseren og overstyrte froeet uten
 * at noe i flata kunne roere den. En overstyring maa alltid ha en vei ut.
 */
export function glemRolle(): void {
  try {
    localStorage.removeItem(NOKKEL);
    location.reload();
  } catch {
    // Ingenting aa gjoere.
  }
}

/** Staar flata paa en overstyrt rolle naa? Stillaset sier det selv. */
export function rolleErOverstyrt(): boolean {
  try {
    return localStorage.getItem(NOKKEL) !== null;
  } catch {
    return false;
  }
}

export function spec(o: Oekt): TimecodeSpec {
  return timecodeSpec(o.production.framerate, o.production.dropFrame);
}
